import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X, AlertTriangle, Sparkles, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const TrialBanner = () => {
  const { user } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Reseller portals (and any tenant-less context, e.g. impersonating a
    // reseller) have no tenant trial — skip the call to avoid noisy 404s.
    if (!user?.tenant_id || user?.reseller_id) {
      setLoading(false);
      return;
    }
    const fetchTrialStatus = async () => {
      try {
        const res = await api.get('/tenant/trial-status');
        setTrialStatus(res.data);
        
        // Check if user dismissed the banner this session
        const dismissedKey = `trial_banner_dismissed_${res.data.trial_end_date}`;
        if (sessionStorage.getItem(dismissedKey)) {
          setDismissed(true);
        }
      } catch (error) {
        console.error('Failed to fetch trial status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrialStatus();
  }, [user]);

  const handleDismiss = () => {
    if (trialStatus?.trial_end_date) {
      sessionStorage.setItem(`trial_banner_dismissed_${trialStatus.trial_end_date}`, 'true');
    }
    setDismissed(true);
  };

  // Don't show anything while loading
  if (loading) return null;

  // Don't show if no trial data, not on trial, or dismissed
  if (!trialStatus || !trialStatus.is_trial || dismissed) return null;

  // Don't show if trial has already expired (they've been downgraded)
  if (!trialStatus.trial_active) return null;

  const daysRemaining = trialStatus.days_remaining;
  const planLabel = trialStatus.plan_name || (trialStatus.plan_level ? trialStatus.plan_level.charAt(0).toUpperCase() + trialStatus.plan_level.slice(1) : 'Pro');

  // Map the trial's plan_level to a Stripe package (Starter -> "basic").
  const PKG_MAP = { starter: 'basic', basic: 'basic', pro: 'pro', enterprise: 'enterprise' };
  const checkoutPackage = PKG_MAP[(trialStatus.plan_level || 'pro').toLowerCase()] || 'pro';

  // One-click: go straight to Stripe checkout for the current trial tier.
  const handleSubscribe = async () => {
    setProcessing(true);
    try {
      const res = await api.post('/billing/checkout', { package: checkoutPackage });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (error) {
      toast.error('Could not start checkout. Please try again.');
      setProcessing(false);
    }
  };
  
  // Determine banner style based on urgency
  let bannerStyle = {
    bg: 'bg-indigo-600',
    text: 'text-white',
    buttonBg: 'bg-white text-indigo-600 hover:bg-indigo-50',
    icon: Sparkles
  };

  if (daysRemaining <= 3) {
    bannerStyle = {
      bg: 'bg-amber-500',
      text: 'text-white',
      buttonBg: 'bg-white text-amber-600 hover:bg-amber-50',
      icon: AlertTriangle
    };
  }

  if (daysRemaining <= 1) {
    bannerStyle = {
      bg: 'bg-red-500',
      text: 'text-white',
      buttonBg: 'bg-white text-red-600 hover:bg-red-50',
      icon: AlertTriangle
    };
  }

  const Icon = bannerStyle.icon;

  return (
    <div className={`${bannerStyle.bg} ${bannerStyle.text} px-4 py-3`} data-testid="trial-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon size={20} className={daysRemaining <= 3 ? 'animate-pulse' : ''} />
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span className="font-medium">
              {daysRemaining === 0 
                ? `Your ${planLabel} trial ends today!` 
                : daysRemaining === 1 
                  ? `Your ${planLabel} trial ends tomorrow!`
                  : `${daysRemaining} days left in your ${planLabel} trial`
              }
            </span>
          </div>
          {daysRemaining <= 7 && (
            <span className="hidden sm:inline text-sm opacity-90">
              Upgrade to keep all your features.
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleSubscribe}
            disabled={processing}
            className={`${bannerStyle.buttonBg} font-medium`}
            data-testid="trial-banner-subscribe-btn"
          >
            {processing ? (
              <><Loader2 size={14} className="mr-1 animate-spin" /> Redirecting…</>
            ) : (
              `Subscribe to ${planLabel}`
            )}
          </Button>
          <Link to="/settings/billing" className="hidden sm:block">
            <button
              className="text-sm underline underline-offset-2 opacity-90 hover:opacity-100 px-1"
              data-testid="trial-banner-compare-link"
            >
              Compare plans
            </button>
          </Link>
          {daysRemaining > 3 && (
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              aria-label="Dismiss banner"
              data-testid="trial-banner-dismiss"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
